/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */
import { css } from 'lit';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import StableSelectorMixin from '../../globals/mixins/stable-selector';
import C4DMarkdown from '../markdown/markdown';
import './content-item-paragraph';
import styles from './content-item.scss';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

const { stablePrefix: c4dPrefix } = settings;

/**
 * The copy content of content item.
 *
 * @element c4d-content-item-copy
 * @csspart paragraph - The paragraph element within the content item copy. Usage: `c4d-content-item-copy::part(paragraph)`
 */
@customElement(`${c4dPrefix}-content-item-copy`)
class C4DContentItemCopy extends StableSelectorMixin(C4DMarkdown) {
  protected get _customTags() {
    const tags = new Set(super._customTags);
    tags.add(`${c4dPrefix}-content-item-paragraph`);
    return tags;
  }

  protected get _renderer() {
    this.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        this.removeChild(node);
      }
    });

    return Object.assign(super._renderer, {
      paragraph(text) {
        return `<${c4dPrefix}-content-item-paragraph part="paragraph">${text}</${c4dPrefix}-content-item-paragraph>`;
      },
    });
  }

  static get stableSelector() {
    return `${c4dPrefix}--content-item__copy`;
  }

  // `styles` here is a `CSSResult` generated by custom WebPack loader
  static get styles() {
    return css`
      ${super.styles}${styles}
    `;
  }
}

/* @__GENERATE_REACT_CUSTOM_ELEMENT_TYPE__ */
export default C4DContentItemCopy;
